/**
 * Shared Product/Model config edit-case helpers (Angular property-edit / property-model-config).
 * `now` is America/New_York wall clock (Angular getCurrentEasternTime). Form/API datetimes are
 * naive local wall clock (Angular matDatepicker Date / moment 'YYYY-MM-DD HH:mm:ss') — do not
 * re-convert those through Eastern or the Active day can shift outside ET timezones.
 */

import type { ConfigDateCase, ConfigEditMode, ConfigRowDates } from '../types'
export type { ConfigDateCase, ConfigEditMode, ConfigRowDates }

/** Angular property-edit / property-model-config — all “now / today” rules use Eastern Time. */
export const EASTERN_TIMEZONE = 'America/New_York' as const

export function toEasternTime(date: Date): Date {
  return new Date(date.toLocaleString('en-US', { timeZone: EASTERN_TIMEZONE }))
}

/** Angular getCurrentEasternTime() */
export function getCurrentEasternTime(): Date {
  return toEasternTime(new Date())
}

/** Angular normalizeDate — day boundary on the Date as-is (no timezone conversion). */
export function normalizeConfigDate(date: Date, endOfDay = false): Date {
  const d = new Date(date)
  if (endOfDay) d.setHours(23, 59, 59, 999)
  else d.setHours(0, 0, 0, 0)
  return d
}

/** Start of today in Eastern wall-clock (Angular normalizeDate(getCurrentEasternTime())). */
export function getEasternTodayStart(): Date {
  return normalizeConfigDate(getCurrentEasternTime())
}

/** Tomorrow at 00:00:00 Eastern — minDate for new Effective From rows. */
export function getTomorrowEasternDate(): Date {
  const d = getEasternTodayStart()
  d.setDate(d.getDate() + 1)
  return d
}

/**
 * Angular getDateFilterEffectiveFrom: selectable only when calendar day is strictly after ET today.
 */
export function isEffectiveFromCalendarDayAllowed(day: Date): boolean {
  return normalizeConfigDate(day).getTime() > getEasternTodayStart().getTime()
}

/**
 * Angular conditionalDateValidator futureDateRequired — `normalizeDate(from) <= normalizeDate(ET now)`.
 * Accepts `YYYY-MM-DD` from form state.
 */
export function isConfigDateStringOnOrBeforeEasternToday(dateStr: string): boolean {
  const parts = dateStr.split('-').map(Number)
  if (parts.length !== 3 || parts.some((p) => Number.isNaN(p))) return false
  const day = new Date(parts[0], parts[1] - 1, parts[2])
  return normalizeConfigDate(day).getTime() <= getEasternTodayStart().getTime()
}

/** Extract calendar day from `YYYY-MM-DD HH:mm:ss` form value for ET day rules. */
export function isConfigDateTimeOnOrBeforeEasternToday(dateTimeLocal: string): boolean {
  const dateOnly = String(dateTimeLocal).trim().match(/^(\d{4}-\d{2}-\d{2})/)?.[1]
  if (!dateOnly) return false
  return isConfigDateStringOnOrBeforeEasternToday(dateOnly)
}

/**
 * Parse form/API naive `YYYY-MM-DD[ HH:mm:ss]` as a local wall-clock Date
 * (same shape Angular gets from matDatepicker / moment).
 */
function parseNaiveLocalDateTime(local: string | null | undefined): Date | null {
  if (!local) return null
  const s = String(local).trim()
  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/,
  )
  if (m) {
    return new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4] ?? 0),
      Number(m[5] ?? 0),
      Number(m[6] ?? 0),
    )
  }
  const d = new Date(s.replace(' ', 'T'))
  return Number.isNaN(d.getTime()) ? null : d
}

/** @deprecated Prefer parseNaiveLocalDateTime for Active badge / day compares. */
function parseLocalToEastern(local: string | null | undefined): Date | null {
  if (!local) return null
  const d = toEasternTime(new Date(String(local).replace(' ', 'T')))
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Angular isDateRangeActive — day-normalized; open To = active once From day has passed.
 * `now` should be getCurrentEasternTime(); from/to are naive local form values (not re-zoned).
 */
export function isConfigDateRangeActive(
  fromLocal: string | null | undefined,
  toLocal: string | null | undefined,
  now: Date = getCurrentEasternTime(),
): boolean {
  const from = parseNaiveLocalDateTime(fromLocal)
  if (!from) return false
  const d = normalizeConfigDate(now)
  const fromDay = normalizeConfigDate(from)
  if (!toLocal) return d.getTime() >= fromDay.getTime()
  const to = parseNaiveLocalDateTime(toLocal)
  if (!to) return d.getTime() >= fromDay.getTime()
  return d.getTime() >= fromDay.getTime() && d.getTime() <= normalizeConfigDate(to, true).getTime()
}

/**
 * Angular isProductActive:
 * - Base: active iff no other non-Base range is active
 * - Else: most-recent open-ended (no To, From passed) wins; else range active and not overridden
 */
export function isProductConfigRowActive(rowKey: string, rows: ConfigRowDates[]): boolean {
  const now = getCurrentEasternTime()
  const self = rows.find((r) => r.key === rowKey)
  if (!self) return false

  if (self.isBase) {
    return !rows.some((o) => !o.isBase && isConfigDateRangeActive(o.fromLocal, o.toLocal, now))
  }

  const nonBase = rows.filter((r) => !r.isBase)
  if (nonBase.length === 1) {
    return isConfigDateRangeActive(self.fromLocal, self.toLocal, now)
  }

  let best: { key: string; fromDay: number } | null = null
  for (const r of nonBase) {
    if (!r.fromLocal || r.toLocal) continue
    if (!isConfigDateRangeActive(r.fromLocal, null, now)) continue
    const from = parseNaiveLocalDateTime(r.fromLocal)
    if (!from) continue
    const fromDay = normalizeConfigDate(from).getTime()
    if (!best || fromDay > best.fromDay) best = { key: r.key, fromDay }
  }
  if (best && best.key === rowKey) return true

  if (!isConfigDateRangeActive(self.fromLocal, self.toLocal, now)) return false

  const selfFrom = parseNaiveLocalDateTime(self.fromLocal)
  const selfFromDay = selfFrom ? normalizeConfigDate(selfFrom).getTime() : 0
  return !nonBase.some((o) => {
    if (o.key === rowKey || !o.fromLocal || !o.toLocal) return false
    if (!isConfigDateRangeActive(o.fromLocal, o.toLocal, now)) return false
    if (!self.fromLocal || !self.toLocal || !isConfigDateRangeActive(self.fromLocal, self.toLocal, now)) {
      return true
    }
    const oFrom = parseNaiveLocalDateTime(o.fromLocal)
    if (!oFrom) return false
    return normalizeConfigDate(oFrom).getTime() > selfFromDay
  })
}

/**
 * Angular determineCaseType:
 * - CASE_2: From (and To if set) in the future
 * - CASE_1: From in the past/now and To future or open
 * - CASE_3: both past (or only To past)
 */
export function determineConfigDateCase(
  effectiveFromLocal: string | null | undefined,
  effectiveToLocal: string | null | undefined,
): ConfigDateCase {
  const now = getCurrentEasternTime()
  const from = effectiveFromLocal
    ? toEasternTime(new Date(String(effectiveFromLocal).replace(' ', 'T')))
    : null
  const to = effectiveToLocal
    ? toEasternTime(new Date(String(effectiveToLocal).replace(' ', 'T')))
    : null

  const fET = from && !Number.isNaN(from.getTime()) ? from : null
  const tET = to && !Number.isNaN(to.getTime()) ? to : null

  if (fET && tET) {
    if (fET > now && tET > now) return 'CASE_2'
    if (fET <= now && tET > now) return 'CASE_1'
    if (fET < now && tET < now) return 'CASE_3'
  } else if (fET) {
    return fET > now ? 'CASE_2' : 'CASE_1'
  } else if (tET) {
    return tET > now ? 'CASE_2' : 'CASE_3'
  }
  return ''
}

/**
 * Angular isProductSelectDisabled while editing CASE_1 window:
 * saved row with From ≤ now < To → product/model select stays locked.
 */
export function isConfigSelectLockedForActiveCase1(
  fromLocal: string | null | undefined,
  toLocal: string | null | undefined,
  hasMapId: boolean,
): boolean {
  if (!hasMapId || !fromLocal || !toLocal) return false
  const now = getCurrentEasternTime()
  const from = parseLocalToEastern(fromLocal)
  const to = parseLocalToEastern(toLocal)
  if (!from || !to) return false
  return from.getTime() <= now.getTime() && to.getTime() > now.getTime()
}

/**
 * Angular cascade: oneDayBefore @ 23:59:59, but if that is before next row's From, use next From.
 */
export function cascadeEffectiveToForNextRow(
  newFromLocal: string,
  nextFromLocal: string | null | undefined,
): string | null {
  const d = new Date(newFromLocal.replace(' ', 'T'))
  if (Number.isNaN(d.getTime())) return null
  d.setDate(d.getDate() - 1)
  d.setHours(23, 59, 59, 0)
  const oneDayBefore = d

  if (nextFromLocal) {
    const nextFrom = new Date(String(nextFromLocal).replace(' ', 'T'))
    if (!Number.isNaN(nextFrom.getTime()) && oneDayBefore.getTime() < nextFrom.getTime()) {
      return formatLocalDateTime(nextFrom)
    }
  }
  return formatLocalDateTime(oneDayBefore)
}

function formatLocalDateTime(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${y}-${m}-${day} ${hh}:${mm}:${ss}`
}

/** Unwrap Laravel-style list payloads from /api/{product|model}/active/list. */
export function unwrapActiveListPayload(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[]
  if (!data || typeof data !== 'object') return []
  const top = data as Record<string, unknown>
  if (Array.isArray(top.data)) return top.data as Record<string, unknown>[]
  if (top.data && typeof top.data === 'object') {
    const inner = top.data as Record<string, unknown>
    if (Array.isArray(inner.data)) return inner.data as Record<string, unknown>[]
    if (Array.isArray(inner.items)) return inner.items as Record<string, unknown>[]
  }
  if (Array.isArray(top.items)) return top.items as Record<string, unknown>[]
  return []
}

export function makeDefaultBaseProductRow(name = 'Base'): Record<string, unknown> {
  return {
    product_id: 1,
    product_name: name,
    is_default: true,
    effective_from: null,
    effective_to: null,
  }
}

export function makeDefaultBaseModelRow(name = 'SM 04.26'): Record<string, unknown> {
  return {
    model_id: 1,
    model_name: name,
    is_default: true,
    effective_from: null,
    effective_to: null,
  }
}
